// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MOCK_SOPS } from '../mockData';
import type { SOP, SOPStatus } from '../../../types';
import './SOPListing.css';

function getStatusIcon(status: SOPStatus): string {
  switch (status) {
    case 'Active':
      return '\u2705';
    case 'Draft':
      return '\ud83d\udcdd';
    case 'Archived':
      return '\u23f8';
    case 'Pending Approval':
      return '\u23f3';
    default:
      return '';
  }
}

function getStatusClass(status: SOPStatus): string {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Draft':
      return 'draft';
    case 'Archived':
      return 'archived';
    case 'Pending Approval':
      return 'pending';
    default:
      return '';
  }
}

function SOPListing() {
  const { site_id } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const sops = useMemo<SOP[]>(() => MOCK_SOPS, []);

  const filteredSOPs = useMemo(() => {
    return sops.filter(sop => {
      const matchesSearch =
        searchQuery === '' ||
        sop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sop.boundsSummary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sop.updatedBy.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'All' || sop.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [sops, searchQuery, statusFilter]);

  return (
    <div className="sop-listing-container">
      <div className="sop-listing-header">
        <h2>Standard Operating Procedures</h2>
        <Link to={`/site/${site_id}/sop/new`} className="sop-create-btn">
          + Create New SOP
        </Link>
      </div>

      <div className="sop-listing-filters">
        <input
          type="text"
          className="sop-search-input"
          placeholder="Search procedures..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <select
          className="sop-status-select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="All">All</option>
          <option value="Active">Active</option>
          <option value="Draft">Draft</option>
          <option value="Archived">Archived</option>
        </select>
      </div>

      <div className="sop-listing-cards">
        {filteredSOPs.map(sop => (
          <Link key={sop.id} to={`/site/${site_id}/sop/${sop.id}`} className="sop-card-link">
            <div className="sop-card">
              <h3 className="sop-card-title">{sop.name}</h3>
              <p className="sop-card-meta">
                {sop.version} &middot;{' '}
                <span className={`sop-status-badge sop-status-${getStatusClass(sop.status)}`}>
                  {getStatusIcon(sop.status)} {sop.status}
                </span>{' '}
                &middot; Updated {sop.lastUpdated} by {sop.updatedBy}
              </p>
              <p className="sop-card-bounds">Bounds: {sop.boundsSummary}</p>
            </div>
          </Link>
        ))}
      </div>

      <div className="sop-listing-footer">
        Showing {filteredSOPs.length} procedure{filteredSOPs.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

export default SOPListing;
